import type {
  ClientStatisticsEmailConfig,
  ClientStatisticsEmailConfigUpdateRequest
} from '@DigitaleDoerfer/digitale-doerfer-api';
import { useQueryClient } from '@tanstack/react-query';

import { API_CACHE_KEYS } from '@/features/api/constants/cache';
import { useGrapevineOrganizationApi } from '@/features/api/hooks/grapevine-organization/useGrapevineOrganizationApi';
import { useApiMutation } from '@/features/api/hooks/useApiMutation';

type UpdateVariables = {
  organizationId: string;
  clientStatisticsEmailConfigUpdateRequest: ClientStatisticsEmailConfigUpdateRequest;
};

/**
 * Updates the statistics-email configuration of a Gebietskörperschaft.
 * The PUT body requires both `enabled` and `frequency` (both @NotNull); the
 * backend preserves the frequency across disable/re-enable, so the caller can
 * send the last-chosen frequency even when turning statistics emails off.
 */
export const useUpdateStatisticsEmailConfig = () => {
  const queryClient = useQueryClient();
  const api = useGrapevineOrganizationApi();

  return useApiMutation<ClientStatisticsEmailConfig, unknown, UpdateVariables>({
    mutationFn: (variables) => api.updateStatisticsEmailConfig(variables),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: [
          API_CACHE_KEYS.STATISTICS_EMAIL_CONFIG,
          variables.organizationId
        ]
      });
    }
  });
};
